import type { TaskQueue } from "$root/utils";
import { type RwAtom, atomWithCompare, readAtom, writeAtom } from "@mdotm/shadcn/legacy/reactivity";
import { unpromisify } from "@mdotm/shadcn/legacy/utils";
import { unreachable } from "@mdotm/utils/runtime";
import { workspaceActionTrigger } from "../analytics";
import { type WorkspaceAction, actionPersists } from "./action";
import type { WorkspaceEffect } from "./effect";
import { type ReducerDeps, reduce } from "./reducer";
import type { WorkspaceSideEffects } from "./side-effects";
import type { WorkspaceState } from "./state";

export type WorkspaceStore = {
	/**
	 * The one store. Writable only from in here — every consumer gets a read-only derived slice, because a
	 * writable slice handed to a component is how the eighteen bypassing writes come back with nicer syntax.
	 * ONE VALUE, ONE DISPATCH — D0007.
	 */
	state$: RwAtom<WorkspaceState>;
	/**
	 * Applies the action and returns when its write-behind effects have settled.
	 *
	 * The state change is **synchronous**, before the promise exists — that is what keeps the UI instant, and
	 * why a caller whose action declares no effect can ignore the return value.
	 *
	 * **It rejects when an effect fails.** The actions that reach outside the document are the shared-concept
	 * CRUD — `addColumn` and `editColumn` in their shared and custom-attribute forms, `removeColumn {shared}`
	 * — and each of them must be awaited by a call site that reports the failure, either
	 * `runWithErrorReporting` or an `onClickAsync` that puts its button into the error state. `setView`'s two
	 * effects are synchronous local writes and cannot fail, which is why the handful of `void
	 * dispatch({type: "setView"})` sites are safe as they are.
	 */
	dispatch(action: WorkspaceAction): Promise<void>;
	/**
	 * Enqueues the whole-document PUT for the current state, which is what every persisted action does.
	 *
	 * Re-running it is safe by construction: the request is the whole document, read at run time, so a second
	 * one carries the state as it is now and subsumes whatever the first was carrying. That is what lets the
	 * toolbar offer this as the Retry beside a failed save, and why it is the store — the author of the write
	 * — that offers it rather than the queue, which cannot know whether a task of its own is repeatable.
	 */
	persist(): Promise<void>;
};

/**
 * The single ingestion point, built out of a switch instead of out of reflection.
 *
 * `useWorkspaceControllers` needed three parallel lists per controller to do this — the methods, an
 * `excludeMethods` deny-list separating reads from writes, and a method-name→trigger map. The union is all
 * three at once: the action *is* the analytics key, `dispatch` *is* the interception point, and a read is
 * not an action, so nothing needs excluding.
 *
 * Order, per dispatch: reduce, then analytics, then the PUT, then the effects. The first is why the UI is
 * instant; the last is last because it is write-behind, and because an effect that dispatches (see
 * `conceptReconciled`) has to find the store already settled.
 */
export function createWorkspaceStore(config: {
	initialState: WorkspaceState;
	/** Sequences the PUTs and reports the "saving" / "saved at" state the toolbar indicator renders. */
	taskQueue: TaskQueue;
	sideEffects: WorkspaceSideEffects;
	reducerDeps?: ReducerDeps;
}): WorkspaceStore {
	const state$ = atomWithCompare(config.initialState);
	const { sideEffects, taskQueue } = config;

	function dispatch(action: WorkspaceAction): Promise<void> {
		// The ids the reducer minted for *this* action, in order. An effect payload is the **request** and
		// nothing else — `createCustomAttributeConcept` carries the property, not the placeholder it is about
		// to replace — so what the reducer minted travels with the dispatch instead.
		const minted: string[] = [];
		const mintingDeps: ReducerDeps = {
			uuid: () => {
				const id = (config.reducerDeps ?? { uuid: () => crypto.randomUUID() }).uuid();
				minted.push(id);
				return id;
			},
		};

		const { state, effects } = reduce(readAtom(state$), action, mintingDeps);
		writeAtom(state$, state);

		const trigger = workspaceActionTrigger(action);
		if (trigger != null) {
			sideEffects.track(state, trigger);
		}

		if (actionPersists(action)) {
			// Local state is authoritative and the request is a whole document, so a failure needs no rollback:
			// the queue reports it, the toolbar offers {@link WorkspaceStore.persist} as a Retry, and the next
			// edit re-persists everything and subsumes it either way. Hence `catchedPersist`: a dispatch does
			// not wait for the PUT and has nowhere to put its failure.
			catchedPersist();
		}

		return runEffects(effects, minted);
	}

	function persist(): Promise<void> {
		return taskQueue.enqueue(() => sideEffects.persist(readAtom(state$)));
	}

	/**
	 * `persist` for a caller with no way to handle a rejection, which warns instead of leaving one
	 * unhandled. Only `dispatch` is such a caller: `persist` itself is returned to the Retry, whose
	 * button awaits it.
	 */
	const catchedPersist = unpromisify(persist);

	/**
	 * Runs what the action declared, in order, and **fails loudly** — reported *and* rethrown.
	 *
	 * Throwing is the default rather than a hook the consumer can opt into, because the opposite default is
	 * the bug: a write-behind that fails silently lets a create panel close on a success toast it did not
	 * earn, and lets a Delete that deleted nothing look exactly like one that worked. The two shared-column
	 * regressions this area has had were both something failing where nobody was looking, so the cost of
	 * configurability here is that the next consumer gets that for free. The local state keeps the optimistic
	 * entry either way — that is what local-first means here, and the next edit re-persists it.
	 *
	 * The console line survives alongside the throw because it is the only place that names *which* effect
	 * failed; what the call site reports names the operation the user asked for.
	 *
	 * The first failure abandons the rest, deliberately. Only `setView` declares more than one effect, and
	 * both of its two are synchronous local writes, so running the remainder after a failure and aggregating
	 * would buy nothing real — while handing every single-effect action's call site an `AggregateError` in
	 * place of the API error it reports on.
	 */
	async function runEffects(effects: WorkspaceEffect[], minted: string[]): Promise<void> {
		for (const effect of effects) {
			try {
				await runEffect(effect, minted);
			} catch (error) {
				console.error(`workspace effect "${effect.effect}" failed`, error);
				throw error;
			}
		}
	}

	async function runEffect(effect: WorkspaceEffect, minted: string[]): Promise<void> {
		switch (effect.effect) {
			case "createSharedConcept": {
				// The response is the tenant's shared pool, and it is authoritative — the local entry the reducer
				// already added is the optimistic half of the same thing.
				const concepts = await sideEffects.sharedConcepts.createSharedConcept(effect.concept);
				await dispatch({ type: "sharedConceptsReplaced", concepts });
				return;
			}
			case "createCustomAttributeConcept": {
				// The one write-behind that cannot be fully local-first: `conceptId` names a concept *definition*
				// the backend creates as a side effect of the custom attribute, so the reducer is holding a
				// placeholder until this returns and `conceptReconciled` repoints the column at the real thing.
				// The placeholder is the only id this action mints.
				const placeholderUuid = minted[0];
				const { concept } = await sideEffects.sharedConcepts.createCustomAttributeConcept(effect.property);
				await dispatch({ type: "conceptReconciled", placeholderUuid, concept });
				return;
			}
			case "updateSharedConcept":
				await sideEffects.sharedConcepts.updateSharedConcept(effect.parameterizedConceptUuid, effect.concept);
				return;
			case "updateCustomAttributeConcept":
				await sideEffects.sharedConcepts.updateCustomAttributeConcept(effect.parameterizedConceptUuid, effect.property);
				return;
			case "renameCustomAttributeTagOption": {
				// The rewrite waits for the transaction: the grid's data request is keyed on the filters, so a
				// criterion rewritten before this returns is asked of a backend that still holds the old value.
				// See the note on `tagOptionRenamed`.
				await sideEffects.sharedConcepts.renameCustomAttributeTagOption(
					effect.parameterizedConceptUuid,
					effect.currentTagValue,
					effect.newTagValue,
				);
				await dispatch({
					type: "tagOptionRenamed",
					parameterizedConceptUuid: effect.parameterizedConceptUuid,
					currentTagValue: effect.currentTagValue,
					newTagValue: effect.newTagValue,
				});
				return;
			}
			case "deleteSharedConcept":
				await sideEffects.sharedConcepts.deleteSharedConcept(effect.concept);
				return;
			case "scrollToTop":
				sideEffects.scrollToTop();
				return;
			case "setMaxPageHeight":
				sideEffects.setMaxPageHeight(effect.maxPageHeight);
				return;
			default:
				return unreachable(effect);
		}
	}

	return { state$, dispatch, persist };
}
